import type { ResumeDocument } from '@docbee/resume';
import { generatorNames } from '@docbee/resume';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { lazy, Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/Button';
import Hello from '@/components/Hello';
import { DownloadIcon, EditorIcon } from '@/components/Icons';
import type { PreviewRef } from '@/components/Resume/Preview';
import { StaticPDFViewer } from '@/components/StaticPDFViewer';
import { clearExamplePDFCache, db, getOrCreateResume } from '@/lib/db';
import { useResume } from '@/lib/hooks';
import { usePDFCache } from '@/lib/usePDFCache';
import { setPersistedState, usePersistedState } from '@/lib/usePersistedState';

const Preview = lazy(() =>
  import('@/components/Resume/Preview').then((mod) => ({ default: mod.Preview }))
);
const PageSelector = lazy(() =>
  import('@/components/Resume/Preview').then((mod) => ({ default: mod.PageSelector }))
);

export const Route = createFileRoute('/')({
  component: Home,
});

function Home() {
  const exampleID = 'example';
  const previewRef = useRef<PreviewRef>(null);

  // Always call useResume to satisfy the Rules of Hooks.
  // Pass isEdited as a refresh key so data reloads when we switch to dynamic mode.
  const isEdited = usePersistedState('docbee:example:edited', false);
  const resumeData = useResume(exampleID, isEdited);

  // Local generator state for the home page UI; synced with IndexedDB.
  const [generator, setGenerator] = useState(resumeData?.generator ?? 'block');

  // Keep local generator in sync with the persisted value from useResume.
  if (resumeData?.generator && generator !== resumeData.generator) {
    setGenerator(resumeData.generator);
  }

  const { cacheState, handleRendered } = usePDFCache(exampleID, isEdited, resumeData);

  const handleDownloadPDF = useCallback(async () => {
    // Dynamic mode with live preview: use the Preview component's download.
    if (cacheState.status === 'miss' && previewRef.current) {
      previewRef.current.download();
      return;
    }

    // Cached mode or static mode: fetch/load the PDF and trigger download.
    try {
      if (cacheState.status === 'hit') {
        // Use the cached blob URL directly.
        const anchor = document.createElement('a');
        anchor.href = cacheState.blobURL;
        anchor.download = 'example.pdf';
        anchor.click();
      } else {
        // Static mode: fetch the pre-rendered PDF from the server.
        const response = await fetch('/pdfs/example.pdf');
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'example.pdf';
        anchor.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Failed to download PDF:', err);
    }
  }, [cacheState]);

  const handleGeneratorChange = useCallback(
    async (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newGenerator = e.target.value;
      setGenerator(newGenerator);

      if (!isEdited) {
        // In static mode: pre-save the generator to IndexedDB so that when
        // useResume reloads (triggered by isEdited flipping), it picks up the
        // correct generator. Then switch to dynamic rendering.
        const record = await getOrCreateResume('example');
        void db.resumes.update(record.id, { generator: newGenerator });
        setPersistedState('docbee:example:edited', true);
      } else if (resumeData) {
        resumeData.setGenerator(newGenerator);
      }

      // Invalidate the cache when the generator changes.
      void clearExamplePDFCache();
    },
    [isEdited, resumeData]
  );

  const navigate = useNavigate();

  // Determine which viewer to show based on cache state.
  const viewer = useMemo(() => {
    if (!isEdited) {
      // First visit: show the pre-rendered static PDF.
      return <StaticPDFViewer url="/pdfs/example.pdf" />;
    }

    if (!resumeData?.isLoaded) {
      return <div>Loading...</div>;
    }

    if (cacheState.status === 'checking') {
      // Cache check in progress — show a loading state instead of starting
      // an expensive dynamic render. This avoids a flash of the live Preview
      // when a cached PDF is available.
      return <div>Loading preview...</div>;
    }

    if (cacheState.status === 'hit') {
      // Cache hit: show the cached PDF for instant loading.
      return <StaticPDFViewer url={cacheState.blobURL} pageSelector={PageSelector} />;
    }

    // Cache miss: render dynamically and cache the result.
    return (
      <Suspense fallback={<div>Loading preview...</div>}>
        <Preview
          ref={previewRef}
          resume={resumeData.parsedResume as ResumeDocument}
          generator={resumeData.generator}
          generatorOptions={resumeData.generatorOptions[resumeData.generator]}
          pageSelector={PageSelector}
          onRendered={handleRendered}
        />
      </Suspense>
    );
  }, [isEdited, resumeData?.isLoaded, cacheState, resumeData, handleRendered]);

  return (
    <div className="flex flex-col p-4 gap-3 mx-auto max-w-4xl w-full">
      <Hello />

      <div className="flex flex-col gap-3 w-full py-2">
        <div className="flex flex-row gap-4 flex-wrap items-center justify-between">
          <Button
            onClick={() => {
              navigate({
                to: `/edit/$resumeId`,
                params: { resumeId: exampleID },
                search: { editor: undefined },
              });
            }}
          >
            <EditorIcon className="text-xs" />
            Edit
          </Button>

          <div className="flex flex-row gap-4">
            <div className="flex flex-row gap-2 items-center">
              <label
                htmlFor="format-select"
                className="text-[10px] md:text-sm text-primary uppercase text-right font-mono"
              >
                Style
              </label>

              <select
                id="format-select"
                className="text-xs font-mono uppercase px-2 py-1 text-primary bg-neutral-50 hover:bg-gray-100 border-b-3 border-x border-t rounded-xs cursor-pointer duration-200 transition-all outline-primary"
                value={generator}
                onChange={handleGeneratorChange}
              >
                {generatorNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <Button onClick={handleDownloadPDF}>
              <DownloadIcon className="size-5" data-slot="icon" />
              PDF
            </Button>
          </div>
        </div>

        {viewer}
      </div>

      <div className="flex flex-col justify-center text-center">
        <Link to="/list" className="font-mono text-sm uppercase underline underline-offset-2">
          List resumes
        </Link>
      </div>
    </div>
  );
}
